import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { NumericFormat } from 'react-number-format'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { useGetAllSubHsHeadingsQuery } from '../../../app/redux-fetch/apiProducts'
import FormDropdown from '../../../components/FormDropDown/FormDropdown'
import RequiredAsterisk from '../../../components/RequiredAsterisk/RequiredAsterisk'
import { SectionLabelTypography } from '../../../components/Typographies/styles'
import { IHsLocalTariff, IHsSubheading } from '../types'
import messages from './messages'
import { getHsLocalTariffValidationSchema } from './productCategoryValidationSchemas'
import { addSubHeading, isValidClassificationCodeInput, removeSubHeading } from './utils/hsCodeUtils'

interface HsLocalTariffModalProps {
  readonly isOpen: boolean
  readonly hsLocalTariffToEdit?: IHsLocalTariff
  readonly handleSubmit?: (hsLocalTariff: IHsLocalTariff) => void
  readonly handleCancel: () => void
}

interface ValidationErrors {
  [key: string]: string
}

const emptyHsLocalTariff: IHsLocalTariff = {
  description: '',
  classificationCode: '',
}

const HsLocalTariffModal: React.FC<HsLocalTariffModalProps> = memo(({
  hsLocalTariffToEdit,
  isOpen,
  handleSubmit,
  handleCancel,
}) => {
  const { formatMessage } = useIntl()
  const { tenantId } = useLoggedInUser()
  const { watch } = useFormContext()
  const values = watch()

  const [hsLocalTariff, setHsLocalTariff] = useState<IHsLocalTariff>(
    hsLocalTariffToEdit ?? emptyHsLocalTariff
  )
  const [shouldValidate, setShouldValidate] = useState(false)
  const [errors, setErrors] = useState<ValidationErrors>({})

  const { data: countries = [] } = useGetCountriesQuery(undefined, { skip: !isOpen })
  const { data: hsSubheadingItems = [] as IHsSubheading[] } = useGetAllSubHsHeadingsQuery(
    { tenantId },
    { skip: !isOpen },
  )

  const isDisabled = !handleSubmit
  const hsLocalTariffSchema = getHsLocalTariffValidationSchema(formatMessage)

  const hsSubheading = hsSubheadingItems.find((x) => x.id === values?.hsSubheadingId)?.subheadingCode ?? ''

  const validateHsLocalTariff = useCallback((tariff: IHsLocalTariff, validate: boolean) => {
    if (!validate) {
      setErrors({})
      return
    }

    const result = hsLocalTariffSchema.safeParse(tariff)
    if (result.success) {
      setErrors({})
    } else {
      const fieldErrors: ValidationErrors = {}
      result.error.errors.forEach((error) => {
        if (error.path.length > 0) {
          fieldErrors[error.path[0]] = error.message
        }
      })
      setErrors(fieldErrors)
    }
  }, [hsLocalTariffSchema])

  const handleFormSubmit = useCallback(async () => {
    setShouldValidate(true)
    const result = hsLocalTariffSchema.safeParse(hsLocalTariff)

    if (result.success) {
      const updatedTariff = { ...hsLocalTariff }
      if (
        !updatedTariff.classificationCode ||
        updatedTariff.classificationCode.substring(0, hsSubheading.length) !== hsSubheading
      ) {
        updatedTariff.classificationCode = addSubHeading(
          updatedTariff.classificationCode || '',
          hsSubheading
        )
      }

      handleSubmit?.(updatedTariff)
      setHsLocalTariff(emptyHsLocalTariff)
      setErrors({})
      setShouldValidate(false)
    } else {
      const fieldErrors: ValidationErrors = {}
      result.error.errors.forEach((error) => {
        if (error.path.length > 0) {
          fieldErrors[error.path[0]] = error.message
        }
      })
      setErrors(fieldErrors)
    }
  }, [hsLocalTariff, hsLocalTariffSchema, hsSubheading, handleSubmit])

  const handleCancelBtn = useCallback(() => {
    setErrors({})
    setShouldValidate(false)
    handleCancel()
  }, [handleCancel])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (name === 'classificationCode') {
      if (!isValidClassificationCodeInput(removeSubHeading(value, hsSubheading))) {
        return
      }
    }

    setHsLocalTariff(prev => ({ ...prev, [name]: value }))
  }, [hsSubheading])

  const handleTariffChange = useCallback((values: any) => {
    setHsLocalTariff(prev => ({ ...prev, tariff: values.floatValue }))
  }, [])

  const handleCountryChange = useCallback((e: any, name: any, newValue: any) => {
    setHsLocalTariff(prev => ({ ...prev, countryId: newValue }))
  }, [])

  useEffect(() => {
    if (hsLocalTariffToEdit) {
      setHsLocalTariff(hsLocalTariffToEdit)
    } else {
      setHsLocalTariff(emptyHsLocalTariff)
    }
  }, [hsLocalTariffToEdit])

  useEffect(() => {
    validateHsLocalTariff(hsLocalTariff, shouldValidate)
  }, [hsLocalTariff, shouldValidate, validateHsLocalTariff])

  const modalTitle = isDisabled
    ? formatMessage(messages.localTariffLbl)
    : formatMessage(messages.editTariffLbl)

  const submitButtonText = hsLocalTariffToEdit
    ? formatMessage(messages.updateBtn)
    : formatMessage(messages.addBtn)

  return (
    <Dialog open={isOpen} onClose={handleCancel}>
      <DialogTitle>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '20px',
          }}
        >
          {modalTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ minWidth: '450px' }}>
        <Box sx={{ mb: '16px' }}>
          <Box sx={{ mb: '16px' }}>
            <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
              {formatMessage(messages.description)}
              <RequiredAsterisk />
            </SectionLabelTypography>
            <TextField
              fullWidth
              id='description'
              name='description'
              value={hsLocalTariff?.description ?? ''}
              onChange={handleChange}
              disabled={isDisabled}
              helperText={errors?.description}
              error={Boolean(errors?.description)}
            />
          </Box>

          <Box sx={{ mb: '16px' }}>
            <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
              {formatMessage(messages.classificationCode)}
            </SectionLabelTypography>
            <TextField
              fullWidth
              id='classificationCode'
              name='classificationCode'
              onChange={handleChange}
              value={removeSubHeading(hsLocalTariff?.classificationCode ?? '', hsSubheading)}
              helperText={errors?.classificationCode}
              disabled={isDisabled}
              error={Boolean(errors?.classificationCode)}
              InputProps={{
                startAdornment: <InputAdornment position='start'>{hsSubheading}</InputAdornment>,
              }}
            />
          </Box>

          <Box sx={{ mb: '16px' }}>
            <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
              {formatMessage(messages.tariff)}
              <RequiredAsterisk />
            </SectionLabelTypography>
            <NumericFormat
              customInput={TextField}
              fullWidth
              decimalScale={4}
              id='tariff'
              name='tariff'
              thousandSeparator
              type='text'
              allowNegative={false}
              onValueChange={handleTariffChange}
              value={hsLocalTariff.tariff}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 11 }}
              disabled={isDisabled}
              error={Boolean(errors?.tariff)}
              helperText={errors?.tariff}
            />
          </Box>

          <Box sx={{ mb: '16px' }}>
            <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
              {formatMessage(messages.country)}
              <RequiredAsterisk />
            </SectionLabelTypography>
            <FormDropdown
              id='countryId'
              items={countries}
              disabled={isDisabled}
              onChange={handleCountryChange}
              value={countries.find((x: any) => x.id === hsLocalTariff?.countryId)?.id}
              error={Boolean(errors?.countryId)}
              errorText={errors?.countryId}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='primary' variant='outlined' onClick={handleCancelBtn}>
          {formatMessage(messages.cancelBtn)}
        </Button>
        {!isDisabled && (
          <Button color='primary' variant='contained' onClick={handleFormSubmit}>
            {submitButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
})

HsLocalTariffModal.displayName = 'HsLocalTariffModal'

export default HsLocalTariffModal
